/**
 * ============================================================================
 * MONTH VIEW v4.1 - PLANIVIO COLOR DOTS
 * ============================================================================
 *
 * ✅ v4.1: Planivio Color Dots
 *          - Farbpunkte statt Text-Events (cal-month-cell-dots)
 *          - Bis zu 4 Dots pro Tag, dann "+X" Anzeige
 *          - Hover-Effekt auf Dots
 *
 * ✅ v4.0: CSS Class-Based Redesign
 *          - Static inline styles → CSS classes (cal-month- namespace)
 *          - Dark mode via .calendar-redesign.dark
 *
 * ✅ v3.0: Planivio Rebranding (Orange → Teal)
 * ✅ v2.1: Header wie WeekView mit Monat und Jahr
 *
 * @version 4.1.0 - Planivio Color Dots
 */

import React from 'react';
import {
  weekDays,
  monthNames,
  getDaysInMonth,
  isToday,
  getAppointmentsForDay
} from './calendarUtils';

console.log('📅 MonthView.js v4.0 loaded - CSS CLASS-BASED + PLANIVIO DESIGN ✓');

const formatTimeCompact = (timeString) => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
};

const getCategoryColor = (category) => {
  const colors = {
    'arzt': '#EF4444', 'medical': '#EF4444',
    'schule': '#22C55E', 'school': '#22C55E',
    'arbeit': '#8B5CF6', 'work': '#8B5CF6',
    'privat': '#3B82F6', 'personal': '#3B82F6',
    'sport': '#06B6D4',
    'familie': '#EC4899', 'family': '#EC4899',
    'sonstiges': '#4A90A4',
    'default': '#3B82F6'
  };
  return colors[category?.toLowerCase()] || colors.default;
};

function MonthView({ currentDate, appointments, onAppointmentClick }) {
  const days = getDaysInMonth(currentDate);

  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  const year = currentDate.getFullYear();

  // First grid date for the date-row preview
  const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const firstGridDate = new Date(firstDay);
  firstGridDate.setDate(firstGridDate.getDate() - startDayOfWeek);

  return (
    <div className="cal-month">

      {/* ===== HEADER ===== */}
      <div className="cal-month-header">
        <span className="cal-month-header-label">
          📆 {monthNames[currentDate.getMonth()]}
        </span>
        <span className="cal-month-header-range">
          01.{month}. - {lastDay.getDate()}.{month}.{year}
        </span>
      </div>

      {/* Weekday Headers */}
      <div className="cal-month-weekday-row">
        {weekDays.map(day => (
          <div key={day} className="cal-month-weekday-name">
            {day.substring(0, 2)}
          </div>
        ))}
      </div>

      {/* Date Row - Erste Woche Vorschau */}
      <div className="cal-month-date-row">
        {weekDays.map((_, idx) => {
          const date = new Date(firstGridDate);
          date.setDate(date.getDate() + idx);
          const day = date.getDate();
          const monthNum = date.getMonth() + 1;
          const isTodayDate = isToday(date);

          return (
            <div
              key={idx}
              className={`cal-month-date-preview ${isTodayDate ? 'cal-month-date-preview--today' : ''}`}
            >
              {day}.{monthNum}.
            </div>
          );
        })}
      </div>

      {/* Days Grid */}
      <div className="cal-month-grid">
        {days.map((dayObj, index) => {
          const dayAppointments = getAppointmentsForDay(appointments, dayObj.date);
          const todayCheck = isToday(dayObj.date);
          const isWeekend = dayObj.date.getDay() === 0 || dayObj.date.getDay() === 6;

          const cellClass = [
            'cal-month-cell',
            !dayObj.isCurrentMonth ? 'cal-month-cell--other' : '',
            todayCheck ? 'cal-month-cell--today' : '',
            isWeekend ? 'cal-month-cell--weekend' : ''
          ].filter(Boolean).join(' ');

          return (
            <div key={index} className={cellClass}>
              {/* Day Number */}
              <div className="cal-month-cell-num-row">
                <span className={`cal-month-cell-num ${todayCheck ? 'cal-month-cell-num--today' : ''}`}>
                  {dayObj.date.getDate()}
                </span>
              </div>

              {/* Appointments as Color Dots */}
              <div className="cal-month-cell-dots">
                {dayAppointments.slice(0, 4).map(apt => (
                  <span
                    key={apt.id}
                    onClick={(e) => { e.stopPropagation(); onAppointmentClick(apt); }}
                    className="cal-month-cell-dot"
                    style={{ background: getCategoryColor(apt.category) }}
                    title={`${formatTimeCompact(apt.time)} - ${apt.title}`}
                  />
                ))}
                {dayAppointments.length > 4 && (
                  <span className="cal-month-cell-more-dots">
                    +{dayAppointments.length - 4}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MonthView;